/**
 * Web Push, browser side. Three questions, answered honestly:
 *
 *   supportLevel()   — can THIS browser do push at all, and if not, why (iOS
 *                      Safari can only once the app is installed to the Home
 *                      Screen — the most common "it doesn't work" on a phone)
 *   currentHolding() — what this browser is holding: an endpoint, nothing,
 *                      or the honest "it could not be asked" that a browser
 *                      the ladder above stopped at is entitled to
 *   subscribe()      — ask permission, register with the browser's push
 *                      service using HQ's VAPID public key, hand the result to
 *                      `push-subscriptions`
 *   unsubscribe()    — the reverse, both sides
 *
 * Nothing here decides WHAT pushes — that is the per-kind switches on the API.
 */

export type PushBlocker =
  | "insecure-context"
  | "no-service-worker"
  | "no-push-manager"
  | "no-notification"
  | "ios-not-installed"
  | "denied";
export type PushSupport =
  | { ok: true; reason?: undefined }
  | { ok: false; reason: PushBlocker };

const isIos = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !(window as unknown as { MSStream?: unknown }).MSStream;

const isStandalone = (): boolean =>
  window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
  (navigator as unknown as { standalone?: boolean }).standalone === true;

export function supportLevel(): PushSupport {
  if (typeof window === "undefined")
    return { ok: false, reason: "no-service-worker" };
  if (!window.isSecureContext) return { ok: false, reason: "insecure-context" };
  if (!("serviceWorker" in navigator))
    return { ok: false, reason: "no-service-worker" };
  if (!("Notification" in window))
    return { ok: false, reason: "no-notification" };
  if (isIos() && !isStandalone())
    return { ok: false, reason: "ios-not-installed" };
  if (!("PushManager" in window))
    return { ok: false, reason: "no-push-manager" };
  if (Notification.permission === "denied")
    return { ok: false, reason: "denied" };
  return { ok: true };
}

export const SUPPORT_REASON_TEXT: Record<PushBlocker, string> = {
  "insecure-context": "Push needs HTTPS.",
  "no-service-worker":
    "This browser cannot run the background worker push needs.",
  "no-push-manager": "This browser does not support Web Push.",
  "no-notification": "This browser cannot show notifications.",
  "ios-not-installed":
    "On iPhone and iPad, push only works once HQ is added to the Home Screen. Share → Add to Home Screen, then open it from there.",
  denied:
    "Notifications are blocked for this site in the browser. Allow them in the site settings, then try again.",
};

/** VAPID keys are base64url; PushManager wants the raw bytes. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export interface BrowserSubscription {
  endpoint: string;
  expirationTime: number | null;
  keys: { p256dh: string; auth: string };
}

const toJson = (sub: PushSubscription): BrowserSubscription => {
  const j = sub.toJSON();
  if (!j.endpoint || !j.keys?.p256dh || !j.keys?.auth)
    throw new Error("The browser returned an incomplete push subscription.");
  return {
    endpoint: j.endpoint,
    expirationTime: j.expirationTime ?? null,
    keys: { p256dh: j.keys.p256dh, auth: j.keys.auth },
  };
};

/** The registration vite-plugin-pwa made; waits for it to be active. */
export const workerRegistration = (): Promise<ServiceWorkerRegistration> =>
  navigator.serviceWorker.ready;

/**
 * Why a question about what this browser holds went unanswered.
 *
 * `answer-stale` is the odd one, and it is not a failure: the browser DID
 * answer, and by the time the caller acted on that answer it was no longer
 * true — `unsubscribeBrowser` found the named endpoint was not on the browser
 * any more. What the browser holds NOW has not been asked, which is exactly
 * what `unavailable` means, and the one thing it must not collapse into is
 * `none`.
 */
export type HoldingUnavailableReason =
  | PushBlocker
  | "ask-failed"
  | "not-asked"
  | "answer-stale";

/**
 * WHAT THIS BROWSER SAYS IT IS HOLDING — three answers, not two, and they are
 * decided HERE because this is the only place that knows which one it is.
 *
 *   held        — `getSubscription()` was called, and named an endpoint
 *   none        — `getSubscription()` was called, and the browser holds nothing
 *   unavailable — the browser was not asked, or could not answer. EVERY
 *                 `supportLevel()` blocker lands here: the ladder returns above
 *                 `getSubscription()`, so on that path nothing about the device
 *                 has been looked at. `denied` is the sharp one — revoking the
 *                 notification permission does NOT unsubscribe a
 *                 `PushSubscription` the browser already made, and one that
 *                 survives is still VALID, so it never 404s and nothing prunes
 *                 it server-side either.
 *
 * Collapsing `unavailable` into `none` — which answering `null` to all of it
 * did — is the fail-open this shape exists to remove. `NotificationSettingsStore`
 * carried the three-valued type and could not populate it, because the function
 * it asked gave the same `null` to "holds nothing" and to "was never asked";
 * the settings page then drew a plain Off switch over somebody else's live
 * subscription.
 *
 * A browser that cannot be REACHED rejects rather than answering — both
 * `navigator.serviceWorker.ready` and `getSubscription()` can, and a
 * subscription the browser returns incomplete throws in `toJson`. The caller
 * bounds this call by a deadline and reads a rejection as `unavailable`;
 * swallowing it here would take that decision away from it.
 */
export type BrowserHolding =
  | { kind: "held"; endpoint: string; subscription: BrowserSubscription }
  | { kind: "none" }
  | { kind: "unavailable"; reason: HoldingUnavailableReason };

/** What this browser currently holds — or that it could not be asked. */
export async function currentHolding(): Promise<BrowserHolding> {
  const support = supportLevel();
  // NOT `none`. `getSubscription()` is below this line, so on this path the
  // browser has not been asked anything at all.
  if (!support.ok) return { kind: "unavailable", reason: support.reason };
  const reg = await workerRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { kind: "none" };
  const subscription = toJson(sub);
  return { kind: "held", endpoint: subscription.endpoint, subscription };
}

/** Ask, then subscribe. Throws with a sentence a person can act on. */
export async function subscribeBrowser(
  vapidPublicKey: string,
): Promise<BrowserSubscription> {
  const support = supportLevel();
  if (support.ok === false)
    throw new Error(SUPPORT_REASON_TEXT[support.reason]);
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error(SUPPORT_REASON_TEXT.denied);
  const reg = await workerRegistration();
  const existing = await reg.pushManager.getSubscription();
  if (existing) return toJson(existing);
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(
      vapidPublicKey,
    ) as unknown as BufferSource,
  });
  return toJson(sub);
}

/**
 * TELL THE BROWSER TO LET GO OF ONE SUBSCRIPTION — THE ONE NAMED. Returns the
 * endpoint it actually released, so the API row can go too.
 *
 * The endpoint is the whole point of the argument. This function used to take
 * none: it called `getSubscription()` and dropped whatever the browser was
 * holding NOW, acting on a decision made about what it was holding THEN. Every
 * caller is a decision about a PARTICULAR subscription ("that one is not this
 * person's", "turn THIS device off"), and between the decision and this call
 * the browser can be holding something else entirely — the next person's,
 * registered on the same browser in the meantime. The deadline the caller wraps
 * this in abandons the call; it cannot cancel it, so a drop decided in a
 * session that has ended still arrives, and unscoped it reached in and
 * unsubscribed the subscription somebody else had just registered.
 *
 * A `PushSubscription` names its own endpoint, so the check costs nothing:
 *
 *   the endpoint asked about  → unsubscribed, and returned
 *   the browser holds nothing → `null`, there was nothing to do
 *   the browser holds ANOTHER → `null`, and it is LEFT ALONE. It was no part
 *                               of this decision and nobody has asked about it.
 *
 * The two `null`s are one answer to the caller — "the endpoint you named is not
 * on this browser" — and the caller's own currency check is what keeps a state
 * write decided in an ended session off the arriving person's screen.
 */
export async function unsubscribeBrowser(
  endpoint: string,
): Promise<string | null> {
  if (!("serviceWorker" in navigator))
    // NOT `null`, which is the SAME collapse one function over: null here means
    // "the browser let go, and had nothing to let go of", and that is the answer
    // the caller writes "this browser holds nothing" from. A browser that was
    // never asked has released nothing, so it refuses instead of answering —
    // which the caller reads as a drop that failed, keeping the subscription
    // and the local record rather than forgetting both.
    throw new Error(SUPPORT_REASON_TEXT["no-service-worker"]);
  const reg = await workerRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return null;
  // Somebody else's subscription, or this browser's own next one: not the thing
  // this drop was decided about, and untouched.
  if (sub.endpoint !== endpoint) return null;
  await sub.unsubscribe();
  return sub.endpoint;
}
